import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import PlainText from '@sg/components/PlainText';
import MetricCard from '@/pages/strategic/pages/BusinessManagement/MetricCard';
import {
  PUBLISHING_HEADCOUNT_HEADCOUNT_CARD,
  COEFFICIENT,
} from '../config';
import CardCommon from '@sg/components/CardCommon';
import { rendTitle } from '@sg/utils/commonPageMethod';
import './index.scss';
import RenderOperations from '@sg/components/Chart/Operations';
import { pickOperations } from '@sg/commonData/pickOperations';
import { useIntl } from 'umi';
import { getHeadcountTrend, getHeadcountProjects, updatePublishingHeadcountProjects, getPublishingHeadcountCapsule } from '@sg/services/index';
import { xAxisHighLight, chartConfig } from '@sg/utils/chatUtils';
import moment from 'moment';
import { chartAllColorsObj, chartOrderedColors } from '@sg/utils/useChartOptions'
import Chart from '@sg/components/Chart';
import TableContent from '@sg/components/TableContent';
import { expandable, injectable, scrollable } from 'tea-component/lib/table/addons';
import { mathFormat, getCurrentQuarters, toTwoDecimalNumber } from '@sg/utils/common';
import TableForm from '@sg/components/TableForm';
import { localizeLabel } from '@sg/utils/translate';
import PlusIcon from '@sg/assests/svg/plus-icon.svg';
import NoData from '@/pages/intelligence/components/NoData';
import Loading from '@intelligence/components/Loading';
import {
  getHeadRateColumns,
  getDefaultHeadcountColumns,
  getPublishingSubtotalColumns,
  getCapabilityBoxSubtotalColumns,
  getStudiosSubtotalColumns,
  generateProjectHeadcountColumns,
  generateProjectHeadcountData,
  generateEditProjectHeadcountColumns,
} from './config';
import { Button, Select, Table, notification } from 'tea-component';
const { draggable } = Table.addons;
import { getAppendixNotesPermission, getButtonPermission } from "@sg/_config/permission";
import { usePermissionReady } from '@sg/hooks/usePermissionReady';
import { createBtnPoint, SGBuriedBtnPoint, PublishingHeadcountTab } from '@/pages/strategic/utils/buriedPoint';
import { EDIT_PAGE_KEY_MAP } from '@sg/hooks/useAutoSaveParams';
import AutoSaveModal from '@sg/components/AutoSaveModal';
import RoutePrompt from '@sg/components/RoutePrompt';
import { useHeadcountAutoSave } from './hooks/useHeadcountAutoSave';
import { defaultCategoryPredicate } from '@sg/utils/tableExpandUtils';
import { useExpandCollapse } from '@/pages/strategic/hooks/useExpandCollapse';

const DefaultFinancialsChat = {
  icon: "discount-chart",
  value: "bar",
  tip: "bar",
  format: false,
};
const height = '343px';
const showFinancialsList = ['bar', 'table'];
export default function Headcount(props: any) {
  const { selectedMonth, onEditingChange, maxDataTime } = props;
  const { formatMessage } = useIntl();
  const { ready: permissionReady } = usePermissionReady();

  // Convert selectedMonth string to moment object for PlainText component // not affected by headcountCapsuleData.date
  const selectedMonthMomentAppendix = useMemo(() => {
    return selectedMonth ? moment(selectedMonth, 'YYYY-MM') : null;
  }, [selectedMonth]);

  // State declarations
  const [headRateData, setHeadRateData] = useState<any>({});
  const [headcountWithChartData, setHeadcountWithChartData] = useState([]);
  const [showHeadRateChatsType, setShowHeadRateChatsType] = useState<any>(DefaultFinancialsChat);
  const [headcountCapsuleData, setHeadcountCapsuleData] = useState<any>(null);

  // Convert selectedMonth string to moment object for PlainText component
  // Use headcountCapsuleData.date from API if available, otherwise fall back to selectedMonth
  const selectedMonthMoment = useMemo(() => {
    const dateStr = headcountCapsuleData?.date || selectedMonth;
    return dateStr ? moment(dateStr, 'YYYY-MM') : null;
  }, [headcountCapsuleData?.date, selectedMonth]);

  // Compute params for PlainText Notes component
  // If headcountCapsuleData.date exists, don't include logical_flag (use date from selectedMonthMoment)
  // If headcountCapsuleData.date doesn't exist, include logical_flag to get latest data
  const notesParams = useMemo(() => {
    const baseParams: any = {
      source: "headcount",
    };
    
    // Only include logical_flag if we don't have headcountCapsuleData.date
    if (!headcountCapsuleData?.date) {
      baseParams.logical_flag = "has_date_latest";
    }
    
    return baseParams;
  }, [headcountCapsuleData?.date]);

  // Handle Headcount Trend chart type change with buried point tracking
  const handleShowHeadRateChatsTypeChange = (e: any, downloadValue?: string) => {
    // Track buried point based on the selected value
    // For download, the second parameter is 'download' (only track when downloadValue is provided)
    // For bar/table, check e.value (but not when it's a download call)
    if (downloadValue === 'download') {
      // Only track download when the second parameter is explicitly 'download'
      // This prevents double tracking since RenderOperations calls change() twice for download
      const btnPoint = createBtnPoint(
        PublishingHeadcountTab,
        'headcountTrendDownload',
        'Headcount Trend - Download'
      );
      if (btnPoint) {
        SGBuriedBtnPoint(btnPoint);
      }
    } else if (e?.value && e.value !== 'download') {
      // Track bar/table only when e.value is set and it's not 'download'
      const value = e.value;

      if (value === 'bar') {
        // Track Bar Chart (only if switching to bar, not already on bar)
        if (showHeadRateChatsType?.value !== 'bar') {
          const btnPoint = createBtnPoint(
            PublishingHeadcountTab,
            'headcountTrendBarChart',
            'Headcount Trend - Bar Chart'
          );
          if (btnPoint) {
            SGBuriedBtnPoint(btnPoint);
          }
        }
      } else if (value === 'table') {
        // Track Table (only if switching to table, not already on table)
        // RenderOperations already prevents duplicate clicks, so we can track directly
        const btnPoint = createBtnPoint(
          PublishingHeadcountTab,
          'headcountTrendTable',
          'Headcount Trend - Table'
        );
        if (btnPoint) {
          SGBuriedBtnPoint(btnPoint);
        }
      }
    }

    // Call the original setter
    setShowHeadRateChatsType(e);
  };
  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);
  const expandedKeysRef = useRef<string[]>([]);
  const isTrackingRef = useRef<boolean>(false);

  // Sync ref with state when state changes from other sources
  useEffect(() => {
    expandedKeysRef.current = expandedKeys;
  }, [expandedKeys]);
  const [isNotesEditing, setIsNotesEditing] = useState(false); // Notes PlainText 编辑状态
  const [isAppendixNotesEditing, setIsAppendixNotesEditing] = useState(false); // Appendix Notes PlainText 编辑状态
  const [isEditing, setIsEditing] = useState(false); // 表格编辑状态
  const HPRef = useRef<any>(null); // 表格引用
  const notesPlainTextRef = useRef<any>(null); // Notes PlainText 引用
  const [selectedCount, setSelectedCount] = useState(0); // 选中的项目数量
  const [moveToValue, setMoveToValue] = useState<string | undefined>(undefined); // Move To下拉框的值
  const [HPEditData, setHPEditData] = useState<any[]>([]); // 编辑数据用于生成Move To选项
  const isUpdatingRef = useRef(false); // 标志位，防止onEditDataChange循环调用

  // 生成一级目录选项（用于批量移动下拉框）
  const HeadcountOptions = useMemo(() => {
    return HPEditData
      .filter((item: any) => item.children && item.Project !== 'Total') // 只选择一级目录（有children的），排除Total行
      .map((item: any) => ({
        text: item.Project, // label 是一级目录名
        value: String(item.id), // value 是一级目录 id
      }));
  }, [HPEditData]);

  const buttonPermission = useMemo(() => ({
    notes: getButtonPermission('publishing_headcount_notes'),
    projectHeadcount: getButtonPermission('publishing_headcount_projectHeadcount'),
    Appendix: getButtonPermission('publishing_headcount_Appendix&Notes'),
  }), [permissionReady]);

  // 通用函数：重新计算每个一级目录及总计的汇总数据
  const recalculateStatusTotals = (data: any[], subjectTotalsOverride?: any[]) => {
    if (!Array.isArray(data)) {
      return [];
    }

    const statusRows = data.filter((item: any) => item && item.Project !== 'Total');

    const overrideSubjects = subjectTotalsOverride && subjectTotalsOverride.length ? subjectTotalsOverride : [];
    let subjectNames = overrideSubjects.map((item: any) => item.subject);

    if (!subjectNames.length) {
      const subjectSet = new Set<string>();
      statusRows.forEach((status: any) => {
        (status.children || []).forEach((child: any) => {
          Object.keys(child || {}).forEach((key) => {
            if (!['Project', 'id', 'cover', 'Total'].includes(key) && !key.endsWith('MoM')) {
              subjectSet.add(key);
            }
          });
        });
      });
      subjectNames = Array.from(subjectSet);
    }

    const aggregatedStatuses = statusRows.map((status: any) => {
      const children = Array.isArray(status.children) ? status.children : [];
      const aggregatedStatus: any = {
        ...status,
        children,
        Total: 0,
      };

      subjectNames.forEach((subjectName) => {
        // Special handling for "Model's Suggested HC" - support range values
        if (subjectName === "Model's Suggested HC") {
          let rangeFirstSum = 0;
          let rangeSecondSum = 0;
          let hasRange = false;

          children.forEach((child: any) => {
            const value = child?.[subjectName];
            if (value && typeof value === 'string') {
              // Check if it's a range format: "a-b*"
              const rangeMatch = value.match(/^(\d+(?:\.\d+)?)-(\d+(?:\.\d+)?)\*$/);
              if (rangeMatch) {
                const first = parseFloat(rangeMatch[1]);
                const second = parseFloat(rangeMatch[2]);
                if (!isNaN(first) && !isNaN(second)) {
                  rangeFirstSum += first;
                  rangeSecondSum += second;
                  hasRange = true;
                }
              }
              // Non-range text is ignored for aggregation
            }
          });

          if (hasRange) {
            // Format as "sum1-sum2*"
            aggregatedStatus[subjectName] = `${rangeFirstSum}-${rangeSecondSum}*`;
          } else {
            // No range values found, set to null (display as "-")
            aggregatedStatus[subjectName] = null;
          }
        }
        // Special handling for "Suggested Value"
        else if (subjectName === "Suggested Value") {
          let numericSum = 0;
          let hasNumeric = false;

          children.forEach((child: any) => {
            const value = child?.[subjectName];
            if (value !== null && value !== undefined && value !== '') {
              const strValue = String(value).trim().toLowerCase();

              // Skip "NA" values
              if (strValue === 'na' || strValue === 'n/a') {
                return;
              }

              // Try to parse as number
              const numVal = parseFloat(String(value));
              if (!isNaN(numVal)) {
                numericSum += numVal;
                hasNumeric = true;
              }
              // Text values are ignored for aggregation
            }
          });

          if (hasNumeric) {
            // Format to 1 decimal place
            aggregatedStatus[subjectName] = numericSum.toFixed(1);
          } else {
            // No numeric values, display "0"
            aggregatedStatus[subjectName] = '0';
          }
        }
        // Regular numeric columns
        else {
          let subjectSum = 0;
          children.forEach((child: any) => {
            const value = Number(child?.[subjectName]);
            if (Number.isFinite(value)) {
              subjectSum += value;
            }
          });
          aggregatedStatus[subjectName] = Number(subjectSum.toFixed(2));
        }
      });

      const statusTotal = children.reduce((sum: number, child: any) => {
        const childTotal = Number(child?.Total);
        return sum + (Number.isFinite(childTotal) ? childTotal : 0);
      }, 0);
      aggregatedStatus.Total = Number(statusTotal.toFixed(2));

      return aggregatedStatus;
    });

    const totalsRow: any = {
      Project: 'Total',
      id: 'total',
    };

    if (overrideSubjects.length) {
      let overrideTotal = 0;
      overrideSubjects.forEach((subject: any) => {
        const subjectName = subject.subject;
        const value = Number(subject.total);
        if (Number.isFinite(value)) {
          totalsRow[subjectName] = Number(value.toFixed(2));
          overrideTotal += value;
        } else {
          totalsRow[subjectName] = '-';
        }
      });
      totalsRow.Total = Number(overrideTotal.toFixed(2));
    } else {
      subjectNames.forEach((subjectName) => {
        // Special handling for "Model's Suggested HC" in Total row
        if (subjectName === "Model's Suggested HC") {
          let totalRangeFirstSum = 0;
          let totalRangeSecondSum = 0;
          let hasTotalRange = false;

          aggregatedStatuses.forEach((status: any) => {
            const value = status?.[subjectName];
            if (value && typeof value === 'string') {
              const rangeMatch = value.match(/^(\d+(?:\.\d+)?)-(\d+(?:\.\d+)?)\*$/);
              if (rangeMatch) {
                const first = parseFloat(rangeMatch[1]);
                const second = parseFloat(rangeMatch[2]);
                if (!isNaN(first) && !isNaN(second)) {
                  totalRangeFirstSum += first;
                  totalRangeSecondSum += second;
                  hasTotalRange = true;
                }
              }
            }
          });

          if (hasTotalRange) {
            totalsRow[subjectName] = `${totalRangeFirstSum}-${totalRangeSecondSum}*`;
          } else {
            totalsRow[subjectName] = null;
          }
        }
        // Special handling for "Suggested Value" in Total row
        else if (subjectName === "Suggested Value") {
          let totalNumericSum = 0;
          let hasTotalNumeric = false;

          aggregatedStatuses.forEach((status: any) => {
            const value = status?.[subjectName];
            if (value !== null && value !== undefined && value !== '') {
              const strValue = String(value).trim().toLowerCase();

              // Skip "NA" and "0" values
              if (strValue === 'na' || strValue === 'n/a' || strValue === '0') {
                return;
              }

              const numVal = parseFloat(String(value));
              if (!isNaN(numVal)) {
                totalNumericSum += numVal;
                hasTotalNumeric = true;
              }
            }
          });

          if (hasTotalNumeric) {
            totalsRow[subjectName] = totalNumericSum.toFixed(1);
          } else {
            totalsRow[subjectName] = '0';
          }
        }
        // Regular numeric columns
        else {
          const subjectTotal = aggregatedStatuses.reduce((sum: number, status: any) => {
            const value = Number(status?.[subjectName]);
            return sum + (Number.isFinite(value) ? value : 0);
          }, 0);
          totalsRow[subjectName] = Number(subjectTotal.toFixed(2));
        }
      });

      const overallTotal = aggregatedStatuses.reduce((sum: number, status: any) => {
        const value = Number(status?.Total);
        return sum + (Number.isFinite(value) ? value : 0);
      }, 0);
      totalsRow.Total = Number(overallTotal.toFixed(2));
    }

    return [totalsRow, ...aggregatedStatuses];
  };

  // Dynamic MetricCard config
  const headcountCardConfig = useMemo(() => {
    return PUBLISHING_HEADCOUNT_HEADCOUNT_CARD(headcountCapsuleData);
  }, [headcountCapsuleData]);

  // 动态生成的表格列配置
  const [dynamicHeadRateColumns, setDynamicHeadRateColumns] = useState<any[]>([]);

  // Headcount Chart Data
  const [headcountChartUpdateTime, setHeadcountChartUpdateTime] = useState<string>('');
  const [headcountTrendLoading, setHeadcountTrendLoading] = useState(false);
  const getHeadcountWithChartData = async () => {
    setHeadcountTrendLoading(true);
    try {
      // Build payload with optional datatime parameter (already formatted as "YYYY-MM")
      const payload: { datatime?: string } = {};
      if (selectedMonth) {
        payload.datatime = selectedMonth;
      }

      // 获取新的 API 数据
      const resData = await getHeadcountTrend(payload);
      const subjects = resData?.data?.subjects || [];
      const monthsMap: any = {};
      setHeadcountChartUpdateTime(resData?.data?.date);
      // 遍历每个 subject，按月份聚合数据
      const normalizeSubjectKey = (rawKey: string) => {
        if (!rawKey) return rawKey;
        const key = rawKey.replace(/[\s._-]/g, '').toLowerCase();
        if (key === 'fullyearkpiavg' || key === 'kpi') return 'KPI';
        return rawKey;
      };

      subjects.forEach((subjectItem: any) => {
        const subjectNameRaw = subjectItem.subject; // 原始字段名（用于表格展示）
        const normalizedKey = normalizeSubjectKey(subjectNameRaw); // 规范化后的字段名（用于图表逻辑）
        subjectItem.data.forEach((monthData: any) => {
          const month = monthData.month;
          if (!monthsMap[month]) {
            monthsMap[month] = { date: month };
          }
          // 保留原始字段，供表格使用
          monthsMap[month][subjectNameRaw] = monthData.headcount;
          // 如果原始字段与规范化字段不同（例如 KPI），额外存一份规范化字段
          if (normalizedKey !== subjectNameRaw) {
            monthsMap[month][normalizedKey] = monthData.headcount;
          }
        });
      });

      // 转换为数组并按月份排序
      const transformedData = Object.values(monthsMap).sort((a: any, b: any) =>
        moment(a.date).valueOf() - moment(b.date).valueOf()
      );

      // 分离 KPI 和其他 subjects，确保 KPI 列在最后
      const kpiSubject = subjects.find((s: any) => s.subject === 'full_year_kpi_avg');
      const otherSubjects = subjects.filter((s: any) => s.subject !== 'full_year_kpi_avg');

      // 动态生成表格列配置（包含所有 subjects，KPI 在最后）
      const dynamicColumns = [
        {
          header: formatMessage({ id: 'dbs_date' }),
          dataIndex: 'date',
          key: 'date',
          width: 100,
        },
        // 先添加其他 subjects 的列
        ...otherSubjects.map((subjectItem: any) => {
          const fieldName = subjectItem.subject;
          return {
            header: <div style={{ textAlign: 'right', lineHeight: '20px' }}>{fieldName}</div>,
            title: fieldName, // Plain text for download
            dataIndex: fieldName,
            key: fieldName,
            width: 160,
            render: (record: any) => {
              const value = record?.[fieldName];
              return (
                <div style={{ textAlign: 'right', textWrap: 'nowrap' }}>
                  {value !== null && value !== undefined ? toTwoDecimalNumber(value) : 'NA'}
                </div>
              );
            }
          };
        }),
        // 最后添加 KPI 列
        ...(kpiSubject ? [{
          header: <div style={{ textAlign: 'right', lineHeight: '20px' }}>KPI</div>,
          dataIndex: 'full_year_kpi_avg',
          key: 'full_year_kpi_avg',
          width: 160,
          render: (record: any) => {
            const value = record?.full_year_kpi_avg;
            return (
              <div style={{ textAlign: 'right', textWrap: 'nowrap' }}>
                {value !== null && value !== undefined ? toTwoDecimalNumber(value) : 'NA'}
              </div>
            );
          }
        }] : [])
      ];

      setDynamicHeadRateColumns(dynamicColumns);

      // 表格使用完整数据（包含 KPI）
      setHeadcountWithChartData(transformedData as any);

      // 图表使用过滤后的数据（排除 full_year_kpi_avg）
      const chartData = transformedData.map((row: any) => {
        const { full_year_kpi_avg, FullYearKpiAvg, ...rest } = row;
        return rest;
      });

      const res: any = { data: chartData };
      const chatData = chartConfig(res?.data, showFinancialsList, (data: any) => {
        const datas = data;
        // if (process.env.NODE_ENV !== 'production') {
        //   // eslint-disable-next-line no-console
        //   console.log('[Headcount] series keys', Object.keys(datas));
        //   // eslint-disable-next-line no-console
        //   console.log('[Headcount] KPI series values', datas?.KPI);
        // }
        const chatData = Object.keys(datas).map((keys, index) => {
          const kpiSeriesName = localizeLabel('KPI', formatMessage);
          const tdata: any = {
            name: localizeLabel(keys, formatMessage),
            barGap: '0%',
            tooltipWidth: '240px',
            barWidth: '25%', // )'40%',
            color: chartOrderedColors[index],
            data: datas[keys].map((items: any, i: number) => {
              return {
                value: items,
                tooltipWidth: '240px',
                valueType: items > 1000 ? '' : 'num',
                headSplit: keys === 'KPI',
                // Dynamic tooltip total should follow legend visibility.
                totalFromVisible: true,
                totalFromVisibleExcludeSeriesNames: [kpiSeriesName],
                // Headcount totals are raw counts, not M-currency values.
                totalFromVisibleValueType: 'num',
                names: 'Actual',
              };
            }),
            type: keys === 'KPI' ? 'line' : showHeadRateChatsType?.value,
            stack: keys === 'KPI' ? undefined : 'HeadRate',
            showSymbol: showHeadRateChatsType?.format,
          };
          return tdata;
        },)
        return chatData;
      })
      setHeadRateData(chatData);
    } catch (error) {
      console.error('Failed to fetch headcount trend data:', error);
      notification.error({
        description: 'Failed to load headcount trend data. Please try again.',
      });
    } finally {
      setHeadcountTrendLoading(false);
    }
  };

  // Fetch Headcount Capsule Data
  const fetchHeadcountCapsuleData = async () => {
    try {
      // Build payload with optional datatime parameter (already formatted as "YYYY-MM")
      const payload: { datatime?: string } = {};
      if (selectedMonth) {
        payload.datatime = selectedMonth;
      }

      const response = await getPublishingHeadcountCapsule(payload);
      if (response?.code === 0 && response?.data) {
        setHeadcountCapsuleData(response.data);
        // Update the updateTime from the API response
        // if (response.data.end_date) {
        //   setUpdateTime(response.data.end_date);
        // }
      }
    } catch (error) {
      console.error('Failed to fetch headcount capsule data:', error);
      notification.error({
        description: 'Failed to load headcount capsule data. Please try again.',
      });
    }
  };
  const [headcountColums, setHeadcountColums] = useState<any[]>([]);
  const [headcountEditColums, setHeadcountEditColums] = useState<any[]>([]);
  const [headcountData, setHeadcountData] = useState<any[]>([]);

  // Helper function to find project name by id in headcountData
  const findProjectNameById = useCallback((id: string): string => {
    const findInData = (data: any[]): string | null => {
      for (const record of data) {
        if (String(record.id) === String(id)) {
          // Return Project name
          return record.Project || 'Unknown';
        }
        if (record.children && Array.isArray(record.children)) {
          const found: string | null = findInData(record.children);
          if (found) return found;
        }
      }
      return null;
    };
    return findInData(headcountData) || 'Unknown';
  }, [headcountData]);

  // Internal function to handle expanded keys change with tracking control
  const handleExpandedKeysChangeInternal = useCallback((keys: string[], shouldTrack: boolean = true) => {
    // Prevent double tracking
    if (isTrackingRef.current) {
      setExpandedKeys(keys);
      expandedKeysRef.current = keys;
      return;
    }

    // Use ref to get the previous keys to avoid stale closure issues
    const previousKeys = expandedKeysRef.current;

    // Only track if keys actually changed
    const previousKeysSorted = [...previousKeys].sort().join(',');
    const keysSorted = [...keys].sort().join(',');
    const keysChanged = previousKeysSorted !== keysSorted;

    if (!keysChanged) {
      setExpandedKeys(keys);
      expandedKeysRef.current = keys;
      return;
    }

    // Set tracking flag to prevent double tracking
    isTrackingRef.current = true;

    // Only track buried points if shouldTrack is true (user manually clicked project)
    if (shouldTrack) {
      // Find which project was expanded or collapsed
      const newlyExpanded = keys.filter(key => !previousKeys.includes(key));
      const newlyCollapsed = previousKeys.filter(key => !keys.includes(key));

      // Track expansion
      if (newlyExpanded.length > 0) {
        newlyExpanded.forEach(key => {
          const projectName = findProjectNameById(key);
          const btnPoint = createBtnPoint(
            PublishingHeadcountTab,
            'projectHeadcountTableCollapse',
            `Project Headcount Table Expand - ${projectName}`
          );
          if (btnPoint) {
            SGBuriedBtnPoint(btnPoint);
          }
        });
      }

      // Track collapse
      if (newlyCollapsed.length > 0) {
        newlyCollapsed.forEach(key => {
          const projectName = findProjectNameById(key);
          const btnPoint = createBtnPoint(
            PublishingHeadcountTab,
            'projectHeadcountTableCollapse',
            `Project Headcount Table Collapse - ${projectName}`
          );
          if (btnPoint) {
            SGBuriedBtnPoint(btnPoint);
          }
        });
      }
    }

    // Update the state and ref, then reset tracking flag
    setExpandedKeys(keys);
    expandedKeysRef.current = keys;

    // Reset tracking flag after a short delay to allow state update
    setTimeout(() => {
      isTrackingRef.current = false;
    }, 0);
  }, [findProjectNameById]);

  // Wrapper for expandable addon - always tracks (user clicked project)
  const handleExpandedKeysChange = useCallback((keys: string[], context?: any) => {
    handleExpandedKeysChangeInternal(keys, true);
  }, [handleExpandedKeysChangeInternal]);
  const [HPRefUpdateTime, setHPRefUpdateTime] = useState<string>('');
  const [originalHeadcountData, setOriginalHeadcountData] = useState<any[]>([]); // 保存原始数据用于对比
  const [headcountProjectsLoading, setHeadcountProjectsLoading] = useState(false);
  // 管理每个 subject 的展开状态: { subjectName: teamsColumns[] }
  const [expandedTeamsMap, setExpandedTeamsMap] = useState<Record<string, any[]>>({});
  // 保存原始的 subject_totals 数据
  const [subjectTotalsData, setSubjectTotalsData] = useState<any[]>([]);
  const hasHeadRateChartData = useMemo(() => {
    const chartData = headRateData?.[showHeadRateChatsType?.value];
    if (Array.isArray(chartData)) {
      return chartData.length > 0;
    }
    return Boolean(chartData);
  }, [headRateData, showHeadRateChatsType?.value]);
  
  // Determine whether to show "Model's Suggested HC" column based on year
  const showModelSuggestedHC = useMemo(() => {
    if (!HPRefUpdateTime) return true;
    const year = moment(HPRefUpdateTime, 'YYYY-MM').year();
    return year <= 2025;
  }, [HPRefUpdateTime]);
  
  const displayHeadcountColumns = useMemo(() => {
    return headcountColums.map((column: any) => {
      const { dataIndex, render } = column || {};
      if (
        typeof render === 'function' ||
        typeof dataIndex !== 'string' ||
        dataIndex === 'Project' ||
        dataIndex.toLowerCase().includes('mom')
      ) {
        return column;
      }
      return {
        ...column,
        format: toTwoDecimalNumber,
        render: (record: any) => {
          const value = record?.[dataIndex];
          return (
            <div style={{ textAlign: column?.align || 'right', textWrap: 'nowrap' }}>
              {toTwoDecimalNumber(value)}
            </div>
          );
        }
      };
    });
  }, [headcountColums]);

  // 生成子孙关系信息（用于拖拽）
  const relations = useMemo(() => {
    const relationMap: any = {};
    const buildRelations = (records: any[], parentId = "") => {
      if (!Array.isArray(records)) {
        return;
      }
      for (const record of records) {
        // 跳过 Total 行
        if (record.Project === 'Total' || String(record.id) === 'total') {
          continue;
        }
        if (parentId) {
          relationMap[String(record.id)] = String(parentId);
        }
        if (record.children && Array.isArray(record.children)) {
          buildRelations(record.children, record.id);
        }
      }
    };
    buildRelations(headcountData || []);
    return relationMap;
  }, [headcountData]);

  // 生成可拖拽的行键值列表（排除 Total 行）
  const draggableRowKeys = useMemo(() => {
    const keys: string[] = [];
    const collectKeys = (records: any[]) => {
      if (!Array.isArray(records)) {
        return;
      }
      for (const record of records) {
        const recordId = String(record.id);
        // 排除 Total 行
        if (recordId !== 'total' && record.Project !== 'Total') {
          keys.push(recordId);
        }
        // 递归收集子项的键值
        if (record.children && Array.isArray(record.children)) {
          collectKeys(record.children);
        }
      }
    };
    collectKeys(headcountData || []);
    return keys;
  }, [headcountData]);

  // Create a ref for fetchProjectHeadcountData to avoid circular dependency
  const fetchProjectHeadcountDataRef = useRef<((payload?: any) => Promise<void>) | undefined>();

  // 展开/折叠所有有 children 的目录 (must be defined before useHeadcountAutoSave hook)
  // Headcount uses children property (not isCategory), so use defaultCategoryPredicate
  const { isAllExpanded, toggleExpandAll, expandAllCategoriesWithChildren } = useExpandCollapse({
    expandedKeys,
    setExpandedKeys,
    expandedKeysRef,
    data: headcountData,
    predicate: defaultCategoryPredicate,
  });

  // 🚀 Auto-save for Headcount Project Table - Using custom hook
  const {
    autoSaveVisible,
    hasHeadcountUnsavedChanges,
    handleHeadcountEdit,
    handleAutoSaveCancel,
    handleAutoSaveConfirm,
    clearHeadcountAutoSave,
  } = useHeadcountAutoSave(
    isEditing,
    headcountData,
    HPRef,
    setHeadcountData,
    expandAllCategoriesWithChildren
  );

  const fetchProjectHeadcountData = async (payload?: any) => {
    try {
      setHeadcountProjectsLoading(true);

      // Build the API payload
      let apiPayload = payload || { use_latest_period: true };

      // If selectedMonth is available and payload doesn't already have period, add it
      if (selectedMonth && !payload?.period) {
        apiPayload = {
          ...apiPayload,
          use_latest_period: false,
          period: selectedMonth,
        };
      }

      const res: any = await getHeadcountProjects(apiPayload);
      const { subject_totals, date } = res?.data || {};
      // setSelectedMonth(moment(date));
      setHPRefUpdateTime(date);
      // 保存原始数据
      setSubjectTotalsData(subject_totals || []);
      // 生成数据
      const data = generateProjectHeadcountData(res?.data);
      setHeadcountData(data);
      if (data.length > 1) {
        setExpandedKeys([String(data[1]?.id)]);
      } else {
        setExpandedKeys([]);
      }
      if (Array.isArray(data) && data.length > 1) {
        setExpandedKeys([String(data[1]?.id)]);
      } else {
        setExpandedKeys([]);
      }
      // 保存列配置
      const freshExpandedMap: Record<string, any[]> = {};
      setExpandedTeamsMap(freshExpandedMap);
      
      // Determine whether to show "Model's Suggested HC" column based on year
      const shouldShowModelHC = date ? moment(date, 'YYYY-MM').year() <= 2025 : true;
      
      const columns = generateProjectHeadcountColumns(subject_totals || [], freshExpandedMap, setExpandedTeamsMap, formatMessage, { isAllExpanded, onToggleExpandAll: toggleExpandAll }, shouldShowModelHC);
      setHeadcountColums(columns);

      // 生成编辑列配置
      const editColumns = generateEditProjectHeadcountColumns(subject_totals || [], freshExpandedMap, setExpandedTeamsMap, {}, formatMessage, shouldShowModelHC);
      setHeadcountEditColums(editColumns);

      // 保存原始数据
      setOriginalHeadcountData(JSON.parse(JSON.stringify(data)));
      setHeadcountProjectsLoading(false);
    } catch (error) {
      console.error('Failed to fetch project headcount data:', error);
      notification.error({
        description: 'Failed to load project headcount data. Please try again.',
      });
      setHeadcountProjectsLoading(false);
    }
  };

  // Assign the function to the ref for use in the hook
  fetchProjectHeadcountDataRef.current = fetchProjectHeadcountData;

  // 当展开状态改变时，重新生成列
  useEffect(() => {
    if (subjectTotalsData.length > 0) {
      const columns = generateProjectHeadcountColumns(subjectTotalsData, expandedTeamsMap, setExpandedTeamsMap, formatMessage, { isAllExpanded, onToggleExpandAll: toggleExpandAll }, showModelSuggestedHC);
      const editColumns = generateEditProjectHeadcountColumns(subjectTotalsData, expandedTeamsMap, setExpandedTeamsMap, {}, formatMessage, showModelSuggestedHC);
      setHeadcountColums(columns);
      setHeadcountEditColums(editColumns);
    }
  }, [expandedTeamsMap, isAllExpanded, showModelSuggestedHC]);

  // 处理编辑状态变化
  const handleEditingChange = useCallback((isEditing: boolean) => {
    setIsEditing(isEditing);
    // 进入编辑模式时，展开所有有 children 的目录
    if (isEditing) {
      if (headcountData.length > 0) {
        expandAllCategoriesWithChildren(headcountData);
      }
    } else {
      // 退出编辑时清空选择状态
      setSelectedCount(0);
      setMoveToValue(undefined);
      // Clear auto-save when user cancels/exits edit mode
      clearHeadcountAutoSave();
      // Note: fetchProjectHeadcountData is now handled by TableContent's onCancelRefetch
      // It will only refetch if changes are detected (handled internally by TableContent)
    }

    // Notify parent component about editing state change to disable the date selection
    if (onEditingChange) {
      onEditingChange(isEditing || isNotesEditing || isAppendixNotesEditing);
    }
  }, [headcountData, expandAllCategoriesWithChildren, clearHeadcountAutoSave, onEditingChange, isNotesEditing, isAppendixNotesEditing]);

  // Notes editing change handler
  const handleNotesEditingChange = useCallback((editing: boolean) => {
    setIsNotesEditing(editing);

    // Notify parent component about editing state change to disable the date selection
    if (onEditingChange) {
      onEditingChange(isEditing || isAppendixNotesEditing || editing);
    }
  }, [onEditingChange, isEditing, isAppendixNotesEditing]);

  // Appendix Notes editing change handler
  const handleAppendixNotesEditingChange = useCallback((editing: boolean) => {
    setIsAppendixNotesEditing(editing);

    // Notify parent component about editing state change to disable the date selection
    if (onEditingChange) {
      onEditingChange(isEditing || isNotesEditing || editing);
    }
  }, [onEditingChange, isEditing, isNotesEditing]);

  // 添加项目分类
  const handleAddProject = () => {
    const timestamp = Date.now();
    const newCategory: any = {
      id: `new_category_${timestamp}`,
      Project: '',
      Total: 0,
      children: [], // 添加 children 属性，标识为一级目录（status）
    };

    // 为每个 subject 添加默认值
    subjectTotalsData.forEach((subject: any) => {
      newCategory[subject.subject] = 0;
      newCategory[`${subject.subject}MoM`] = '-';
      // 为每个 team 添加默认值
      subject.teams.forEach((team: any) => {
        newCategory[team.team] = 0;
      });
    });

    if (HPRef.current) {
      // 更新 editData 和 tableData，在顶部添加新行（在Total行之后）
      HPRef.current.setEditData((prevData: any[]) => {
        // 找到Total行
        const totalIndex = prevData.findIndex((item: any) => item.Project === 'Total');
        if (totalIndex !== -1) {
          // 在Total行之后插入新行
          return [
            ...prevData.slice(0, totalIndex + 1),
            newCategory,
            ...prevData.slice(totalIndex + 1)
          ];
        }
        // 如果没有Total行，就添加在开头
        return [newCategory, ...prevData];
      });
      HPRef.current.setTableData((prevData: any[]) => {
        // 找到Total行
        const totalIndex = prevData.findIndex((item: any) => item.Project === 'Total');
        if (totalIndex !== -1) {
          // 在Total行之后插入新行
          return [
            ...prevData.slice(0, totalIndex + 1),
            newCategory,
            ...prevData.slice(totalIndex + 1)
          ];
        }
        // 如果没有Total行，就添加在开头
        return [newCategory, ...prevData];
      });

      // 展开新添加的行
      setExpandedKeys((prevKeys: string[]) => {
        const newId = newCategory.id;
        if (!prevKeys.includes(newId)) {
          return [...prevKeys, newId];
        }
        return prevKeys;
      });
    }
  };

  // 处理保存
  const handleSave = async (editedData: any) => {
    try {
      setHeadcountProjectsLoading(true);

      const apiData: any = {
        categories_to_delete: [],
        categories_to_create: [],
        categories_to_update: [],
        category_orders: [],
        projects_to_move: [],
        project_orders: [],
        metrics: [], // Add metrics array for Model's Suggested HC and Suggested Value changes
        datatime: HPRefUpdateTime,
      };

      // 过滤掉 Total 行
      const originalData = originalHeadcountData.filter((item: any) => item.Project !== 'Total');
      const newData = editedData.filter((item: any) => item.Project !== 'Total');

      // Track if any Suggested Value has changed
      let anySuggestedValueChanged = false;

      // 构建原始数据的 Map（用于快速查找）
      const originalCategoriesMap = new Map();
      const originalProjectsMap = new Map();

      originalData.forEach((category: any) => {
        originalCategoriesMap.set(category.id, category);
        if (category.children && Array.isArray(category.children)) {
          category.children.forEach((project: any) => {
            originalProjectsMap.set(project.id, {
              ...project,
              category: category.id, // 记录原始所属的一级目录（status）
            });
          });
        }
      });

      // 构建编辑后数据的 Map（用于快速查找）
      const editedCategoriesMap = new Map();
      newData.forEach((category: any) => {
        editedCategoriesMap.set(category.id, category);
      });

      // 检查被删除的 categories (status)
      originalData.forEach((originalCategory: any) => {
        if (!editedCategoriesMap.has(originalCategory.id)) {
          apiData.categories_to_delete.push(originalCategory.Project);
        }
      });

      // 检查新增的 categories (status)
      newData.forEach((editedCategory: any) => {
        if (!originalCategoriesMap.has(editedCategory.id)) {
          apiData.categories_to_create.push({
            category_name: editedCategory.Project
          });
        }
      });

      // 检查名字改变的 categories 和记录顺序
      newData.forEach((editedCategory: any, index: number) => {
        const originalCategory = originalCategoriesMap.get(editedCategory.id);
        if (originalCategory) {
          // 检查名字是否改变
          if (originalCategory.Project !== editedCategory.Project) {
            apiData.categories_to_update.push({
              old_category_name: originalCategory.Project,
              new_category_name: editedCategory.Project
            });
          }

          // 记录一级目录（status）顺序
          apiData.category_orders.push({
            category_name: editedCategory.Project, // 使用编辑后的名字
            order: index + 1
          });
        }

        // 检查二级项目的变动
        if (editedCategory.children && Array.isArray(editedCategory.children)) {
          editedCategory.children.forEach((editedProject: any, pIndex: number) => {
            const originalProject = originalProjectsMap.get(editedProject.id);

            // 检查是否是新增的项目（ID以 'new_project_' 开头或不在原始数据中）
            const isNewProject = editedProject.id && (String(editedProject.id).startsWith('new_project_') || !originalProject);

            if (originalProject) {
              // 检查项目是否移动到其他一级目录（status）
              const categoryId = editedCategory.id;
              const isMoved = originalProject.category !== categoryId;

              // 如果移动了，记录到 projects_to_move
              if (isMoved) {
                apiData.projects_to_move.push({
                  project_name: editedProject.Project,
                  new_category_name: editedCategory.Project // 使用编辑后的一级目录名
                });
                // 同时记录在新目录中的排序位置
                apiData.project_orders.push({
                  project_name: editedProject.Project,
                  order: pIndex + 1
                });
              } else {
                // 如果没有移动，记录排序
                apiData.project_orders.push({
                  project_name: editedProject.Project,
                  order: pIndex + 1
                });
              }

              // Normalize values for comparison to avoid false positives
              // Model's Suggested HC: null/undefined/"" are all equivalent to empty
              const normalizeModelHC = (val: any): string => {
                if (val === null || val === undefined || val === '') {
                  return '';
                }
                return String(val);
              };

              // Suggested Value: null/undefined/""/0/"0" are all equivalent to "0"
              const normalizeSuggestedValue = (val: any): string => {
                if (val === null || val === undefined || val === '') {
                  return '0';
                }
                const strVal = String(val).trim();
                // "0" or "0.0" should be normalized to "0"
                const numVal = parseFloat(strVal);
                if (!isNaN(numVal) && numVal === 0) {
                  return '0';
                }
                return strVal;
              };

              // Compare normalized values
              const originalModelHC = normalizeModelHC(originalProject["Model's Suggested HC"]);
              const editedModelHC = normalizeModelHC(editedProject["Model's Suggested HC"]);
              const modelHCChanged = originalModelHC !== editedModelHC;

              const originalSuggestedValue = normalizeSuggestedValue(originalProject["Suggested Value"]);
              const editedSuggestedValue = normalizeSuggestedValue(editedProject["Suggested Value"]);
              const suggestedValueChanged = originalSuggestedValue !== editedSuggestedValue;

              // Track if any Suggested Value has changed
              if (suggestedValueChanged) {
                anySuggestedValueChanged = true;
              }

              if (modelHCChanged || suggestedValueChanged) {
                const metricEntry: any = {
                  project_id: String(editedProject.id),
                };

                if (modelHCChanged) {
                  // Use the normalized value (empty string for null/undefined)
                  metricEntry.model_suggested_hc = editedModelHC;
                }

                if (suggestedValueChanged) {
                  // Use the normalized value ("0" for null/undefined/empty)
                  metricEntry.suggested_value = editedSuggestedValue;
                }

                apiData.metrics.push(metricEntry);
              }
            } else if (isNewProject) {
              // 新增的项目，记录排序
              apiData.project_orders.push({
                project_name: editedProject.Project,
                order: pIndex + 1
              });
            }
          });
        }
      });
      // 调用 API
      await updatePublishingHeadcountProjects({
        ...apiData,
      });

      // Clear auto-save data on successful save
      clearHeadcountAutoSave();

      // Set loading to false immediately since we're not waiting for refetch
      setHeadcountProjectsLoading(false);

      // TableContent will turn off editing mode automatically after this function completes
      // This allows the UI to respond faster while refetch APIs run in background
      const refreshPromises = [
        fetchProjectHeadcountData(),
        fetchHeadcountCapsuleData(),
      ];

      // Only refresh Notes PlainText if Suggested Value has changed
      if (anySuggestedValueChanged) {
        refreshPromises.push(notesPlainTextRef.current?.refresh?.());
      }

      Promise.all(refreshPromises).catch((error) => {
        console.error('Error refetching data after save:', error);
      });

      // Function completes here, allowing TableContent to turn off editing mode immediately
    } catch (error) {
      console.error('保存失败:', error);
      notification.error({
        description: 'Failed to save headcount projects. Please try again.',
      });
      setHeadcountProjectsLoading(false);
    }
  };
  useEffect(() => {
    getHeadcountWithChartData();
    fetchHeadcountCapsuleData();
    fetchProjectHeadcountData();
  }, [formatMessage, selectedMonth]);

  // 筛选器配置
  const formList = [
    {
      key: 'project',
      type: 'Select',
      hidden: true, // Hidden for future enhancement
      componentConfig: {
        button: <div className="dbi-metric-select">
          <div className="dbi-metric-select-lable">
            {formatMessage({ id: 'dbs_project' })}
          </div>
          <div className="dbi-metric-select-text">
            <div className="dbi-metric-select-value">All</div>
          </div>
        </div>,
        defaultValue: 'all',
        options: [
          {
            value: 'all',
            text: 'All'
          },
        ],

        size: 'm',
        searchable: true,
        matchButtonWidth: true,
        appearance: "button",
        listHeight: 600,
        className: 'dbs-metric-select',
        boxClassName: 'dbs-metric-select-overlay',
      }
    },
  ];
  return (
    <div className="dbs-home-publishing-headcount">
      <div className='dbs-home-studio-card'>
        <MetricCard
          key={headcountCardConfig.title}
          coefficient={COEFFICIENT}
          config={headcountCardConfig}
          capsuleMode={'single'}
          capsuleHeight={40}
          valueFormatter={toTwoDecimalNumber}
        />
        <PlainText
          ref={notesPlainTextRef}
          title={formatMessage({ id: 'dbs_notes' })}
          url="/api/v1/mgmt_pc/note/get"
          editUrl="/api/v1/mgmt_pc/note/update"
          params={notesParams}
          showDatePicker={false}
          selectedMonth={selectedMonthMoment}
          maxDataTime={maxDataTime}
          showTimeInTitle={true}
          editable={true}
          userPermissions={{ canEdit: buttonPermission.notes }}
          onEditingChange={handleNotesEditingChange}
          style={{ maxHeight: 283 }}
          tinyEditorHeight={160}
          tipString={'Data is updated by the Business Operations Management team.'}
          onEditClick={(type) => {
            // Track buried point for Notes Edit button or Add Content link click
            const extInfo = type === 'Add Content Link'
              ? 'Notes Edit - Add Content Link'
              : 'Notes Edit - Edit Button';
            const btnPoint = createBtnPoint(
              PublishingHeadcountTab,
              'notesEdit',
              extInfo
            );
            if (btnPoint) {
              SGBuriedBtnPoint(btnPoint);
            }
          }}
          // Auto-save feature
          autoSavePageKey={EDIT_PAGE_KEY_MAP.publishing_headcount_notes}
        />
        {/* <CardCommon
          title={
            rendTitle({ 
              title: formatMessage({ id: 'dbs_notes'}),
              tips: updateTime
            })
          }
        >
          <div className="dbs-scrollable-container">
            <ul className="dbs-notes-list">
              {notes.map((note, index) => (
                <li key={index}>{note}</li>
              ))}
            </ul>
          </div>
        </CardCommon> */}
      </div>

      <CardCommon
        title={rendTitle({ title: formatMessage({ id: 'dbs_projects_headcount' }), tips: headcountChartUpdateTime ? `${formatMessage({ id: 'dbs_update_on' })} ${headcountChartUpdateTime}` : '' })}
        operation={<RenderOperations
          defaultValue={showHeadRateChatsType.value}
          defaultValueShow={showHeadRateChatsType.format}
          change={handleShowHeadRateChatsTypeChange}
          options={pickOperations([...showFinancialsList, 'download'], formatMessage)}
          downloadParams={{
            data: headcountWithChartData,
            columns: dynamicHeadRateColumns,
            name: `Project Headcount`,
          }}
        >
        </RenderOperations>}
      >
        {showHeadRateChatsType?.value !== 'table' ? (
          headcountTrendLoading ? (
            <Loading style={{ height }} />
          ) : hasHeadRateChartData ? (
            <Chart
              data={headRateData[showHeadRateChatsType?.value]}
              key={`HeadRate${showHeadRateChatsType?.value}`}
              type={showHeadRateChatsType?.value}
              height={height}
              numberType={'num'} // to format 0M to 0
              setOtherConfigFunc={(opt: any) => {
                opt.grid.containLabel = true;
                // opt.legend.type="plain"
                opt = xAxisHighLight(opt, {
                  lineStyle: showHeadRateChatsType?.value === 'bar' ? {
                    color: 'rgba(216, 218, 228, 0.3)',
                    width: 94,
                    type: 'solid',
                  } : {
                    color: '#BBBDCB',
                    width: 1,
                    type: 'solid',
                  }
                });
                return opt;
              }}
            ></Chart>
          ) : (
            <div style={{ minHeight: height || '320px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <NoData text={formatMessage({ id: 'dbs_empty_text' })} />
            </div>
          )
        ) : (
          <></>
        )}
        {showHeadRateChatsType?.value === 'table' ? (
          headcountWithChartData.length > 0 ? (
            <div style={{ marginTop: '15px' }}>
              <TableContent
                className="dbs-publishing-headcount-groupable-table"
                datas={headcountWithChartData}
                columns={dynamicHeadRateColumns}
                sort={[{ by: 'fill_time', order: 'desc' }]}
                resultKey="list"
                recordKey="date"
                addons={[
                  // 支持表格滚动，高度超过 192 开始显示滚动条
                  scrollable({
                    // maxHeight: '310px',
                    // minWidth: headcountActive === 'headcount' ? 1400 : 'auto',
                  })
                ]}
              ></TableContent>
            </div>
          ) : (
            <div style={{ minHeight: height || '320px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <NoData text={formatMessage({ id: 'dbs_empty_text' })} />
            </div>
          )
        ) : (
          <></>
        )}
      </CardCommon>

      <div style={{ margin: "20px 0" }} className="dbs-home-publishing-headcount-table">
        <CardCommon
          title={
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                <div>{rendTitle({
                  title: formatMessage({ id: 'dbs_projects_headcount_table' }),
                  tips: isEditing
                    ? `${formatMessage({ id: 'dbs_period' })}: ${HPRefUpdateTime}`
                    : `${formatMessage({ id: 'dbs_update_on' })} ${HPRefUpdateTime}`
                })}</div>
                <div className='dbs-weekly-buttons-wrapper'>
                  {buttonPermission.projectHeadcount && headcountData.length !== 0 && (!isEditing ? (
                    <Button
                      onClick={() => {
                        // Track buried point for Edit button
                        const btnPoint = createBtnPoint(
                          PublishingHeadcountTab,
                          'projectHeadcountTableEdit',
                          'Project Headcount Table - Edit'
                        );
                        if (btnPoint) {
                          SGBuriedBtnPoint(btnPoint);
                        }

                        // Check for auto-save before entering edit mode
                        // handleEditingChange will handle expanding categories when entering edit mode
                        handleHeadcountEdit();
                      }}
                      style={{ height: '30px', minWidth: '60px', fontSize: '12px' }}
                      className="dbs-gradient-btn"
                    >
                      {formatMessage({ id: 'dbs_edit' })}
                    </Button>
                  ) : (
                    <div className="dbs-weekly-buttons-wrapper">
                      <Button
                        onClick={() => {
                          HPRef.current?.handleSave();
                        }}
                        style={{ height: '30px', minWidth: '76px', fontSize: '12px' }}
                        className="dbs-gradient-btn"
                      >
                        {formatMessage({ id: 'dbs_confirm' })}
                      </Button>
                      <Button
                        onClick={() => {
                          // Clear auto-save on cancel
                          clearHeadcountAutoSave();
                          HPRef.current?.handleCancel();
                          if (headcountData.length > 0) {
                            setExpandedKeys([String(headcountData[0]?.id)]);
                          }
                        }}
                        style={{ height: '30px', minWidth: '76px', fontSize: '12px' }}
                        className="dbs-outline-btn"
                      >
                        {formatMessage({ id: 'dbs_cancel' })}
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          }
        >
          {isEditing ? (
            <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
              {/* <TableForm formList={formList}></TableForm> */}
              <Button
                type='weak'
                onClick={handleAddProject}
                className="dbs-add-project-btn"
                style={{ height: '36px', display: 'flex', alignItems: 'center', gap: 4 }}
              >
                <img src={PlusIcon} alt="Add" className="dbs-add-project-icon" style={{ width: 16 }} />
                {formatMessage({ id: 'dbs_add_project_category' })}
              </Button>
              <Select
                options={HeadcountOptions}
                appearance="button"
                placeholder="Move to"
                value={moveToValue}
                disabled={selectedCount === 0}
                listHeight={Math.min(HeadcountOptions.length * 36, 5.5 * 36)}
                onChange={(value: string) => {
                  setMoveToValue(value);
                  if (value && HPRef.current) {
                    HPRef.current.batchMoveToCategory(value);
                    // 移动完成后清空下拉框
                    setTimeout(() => {
                      setMoveToValue(undefined);
                    }, 0);
                  }
                }}
              />
              <span style={{ color: '#888', fontSize: '14px', lineHeight: '36px' }}>
                {selectedCount} {selectedCount === 1 ? 'item' : 'items'} selected
              </span>
            </div>
          ) : (
            <>
            </>
          )}
          <>
            {isEditing && (
              <div style={{ marginBottom: '20px', color: '#262832', fontSize: '14px' }}>
                {formatMessage({ id: 'dbs_headcount_metrics_data_notice' })}
              </div>
            )}
          </>
          <TableContent
            ref={HPRef}
            loading={headcountProjectsLoading}
            className="dbs-headcount-projects-table"
            stickyHeader={true}
            // className="dbs-pub-table-points-content dbs-commons-table"
            // height={'360px'}  className='dbs-table-phase'
            recordKey={(record: any) =>
              // 这里要注意，原始数据中数据类型全是字符串，不转成字符串会导致 key 值匹配不上
              String(record.id)
            }
            // url="/mock/v1/Headcount/list"
            columns={displayHeadcountColumns}
            editColumns={headcountEditColums}
            datas={headcountData}
            addons={[
              expandable({
                // 已经展开的产品
                expandedKeys,
                // 产品展开为消息行
                expand: record => record.children || null,
                // 发生展开行为时，回调更新展开键值 (with buried point tracking)
                onExpandedKeysChange: handleExpandedKeysChange,
                // 只有产品行允许展开（编辑态不显示展开图标）
                shouldRecordExpandable: record => !isEditing && Boolean(record.children),
                targetColumnKey: "Project",
                rowExpand: true
              }),
              injectable({
                row: (props, context) => {
                  return ({
                    style: {
                      ...(props.style || {}),
                      background: context.recordIndex === 0 ? '#DBD8F6' : context.record?.children ? "#F5F5F8" : undefined,
                    },
                  })
                },
                cell: (props, context: any) => {
                  return {
                    style: {
                      ...(props.style || {}),
                      background: context?.column.showHiddenColor ? "rgba(127, 111, 241, 0.06)" : '',
                    }
                  }
                }
              }),
              scrollable({
                maxHeight: '800px',
                minWidth: '200px'
              })
            ]}
            editable={true}
            userPermissions={{ canEdit: buttonPermission.projectHeadcount }}
            hideButtons={true}
            onEditingChange={handleEditingChange}
            enableSelection={true}
            onSelectionChange={(keys: string[]) => {
              setSelectedCount(keys.length);
            }}
            validateFields={['Project']}
            validateFilter={(record: any) => {
              // 只检查一级分类（有 children 的行）
              return record.children && Array.isArray(record.children);
            }}
            onEditDataChange={(data: any) => {
              // 如果正在更新中，跳过此次调用，防止循环
              if (isUpdatingRef.current) {
                return;
              }

              // 设置标志位，表示正在更新
              isUpdatingRef.current = true;

              // 编辑数据变化时，重新计算汇总并更新下拉选项
              const recalculatedData = recalculateStatusTotals(data);
              setHPEditData(recalculatedData);
              // 🔥 CRITICAL: Update TableContent's internal editData to trigger re-render
              if (HPRef.current) {
                HPRef.current.setEditData(recalculatedData);
                HPRef.current.setTableData(recalculatedData);
              }

              // 使用setTimeout确保更新完成后再重置标志位
              setTimeout(() => {
                isUpdatingRef.current = false;
              }, 0);
            }}
            onBatchMoveToCategory={(data: any, targetCategoryId: string) => {
              // 移动后重新计算每个一级目录的汇总数据
              const recalculatedData = recalculateStatusTotals(data);
              // 自动展开目标分类，让用户看到移动后的项目
              setExpandedKeys((prevKeys: string[]) => {
                if (!prevKeys.includes(targetCategoryId)) {
                  return [...prevKeys, targetCategoryId];
                }
                return prevKeys;
              });
              return recalculatedData;
            }}
            // 拖拽addon需要单独处理，通过editingAddons传递
            editingAddons={[
              draggable({
                expandedKeys,
                relations,
                dragType: "tree",
                childrenColName: "children",
                draggableRowKeys: draggableRowKeys, // 只允许拖拽非 Total 的行
                onExpandedKeysChange: (keys: any) => {
                  setExpandedKeys(keys);
                },
                onDragEnd: (records: any, dragContext: any) => {
                  // 保存原始数据以便在失败或取消时恢复展开状态
                  const originalData = headcountData.length > 0 ? headcountData : (HPRef.current?.getTableData?.() || []);

                  try {
                    // 检查拖拽是否被取消（records 为空或无效）
                    if (!records || records.length === 0) {
                      // 拖拽被取消，立即恢复展开状态并返回
                      setTimeout(() => {
                        expandAllCategoriesWithChildren(originalData);
                      }, 100);
                      return;
                    }

                    // 检查是否有任何类别被拖拽到 Total 行之上
                    if (records.length > 0 && String(records[0].id) !== 'total') {
                      // 如果第一个记录不是 Total，检查是否 Total 应该在第一
                      const totalIndex = records.findIndex((item: any) =>
                        String(item.id) === 'total' || item.Project === 'Total'
                      );
                      if (totalIndex > 0) {
                        // Total 不在第一位，说明有类别被拖到它上面了
                        notification.error({
                          description: 'Invalid drag operation. Categories cannot be moved above "Total". "Total" must always be at the top.',
                        });
                        setTimeout(() => {
                          expandAllCategoriesWithChildren(originalData);
                        }, 100);
                        return;
                      }
                    }

                    // 验证拖拽结果，确保项目不会被嵌套到其他项目中，以及目录不会被嵌套到其他目录中
                    const validateDragResult = (data: any[]): { isValid: boolean; errorType: 'category' | 'project' | null } => {
                      const checkNesting = (items: any[], depth: number = 0, parentIsCategory: boolean = false): { isValid: boolean; errorType: 'category' | 'project' | null } => {
                        for (const item of items) {
                          // 跳过 Total 行
                          if (item.Project === 'Total' || String(item.id) === 'total') {
                            continue;
                          }

                          // 判断是否为目录：有 children 属性
                          const isCategory = item.children !== undefined;

                          // 如果是项目（非目录），它不应该有 children
                          if (!isCategory && item.children && Array.isArray(item.children) && item.children.length > 0) {
                            console.warn('Invalid drag: Project cannot have children', item);
                            return { isValid: false, errorType: 'project' };
                          }

                          // 如果是目录，它不应该被嵌套到另一个目录中（目录只能在顶层）
                          if (isCategory && parentIsCategory) {
                            console.warn('Invalid drag: Category cannot be nested inside another category', item);
                            return { isValid: false, errorType: 'category' };
                          }

                          // 递归检查子项
                          if (item.children && Array.isArray(item.children)) {
                            const result = checkNesting(item.children, depth + 1, isCategory);
                            if (!result.isValid) {
                              return result;
                            }
                          }
                        }
                        return { isValid: true, errorType: null };
                      };
                      return checkNesting(data);
                    };

                    // 验证拖拽结果
                    const validationResult = validateDragResult(records);
                    if (!validationResult.isValid) {
                      console.warn('Drag validation failed: Invalid nesting detected.', validationResult);

                      let errorMessage = '';
                      switch (validationResult.errorType) {
                        case 'category':
                          errorMessage = 'Invalid drag operation. Categories cannot be nested inside other categories.';
                          break;
                        case 'project':
                          errorMessage = 'Invalid drag operation. Projects cannot be nested inside other projects.';
                          break;
                        default:
                          errorMessage = 'Invalid drag operation. Please try again.';
                      }

                      notification.error({
                        description: errorMessage,
                      });

                      // 使用 setTimeout 确保在拖拽操作完成后才展开（避免与 draggable addon 的 onExpandedKeysChange 冲突）
                      setTimeout(() => {
                        const currentData = headcountData.length > 0 ? headcountData : (HPRef.current?.getTableData?.() || []);
                        console.log('🔄 [DEBUG] Expanding categories after validation failure, data length:', currentData.length);
                        expandAllCategoriesWithChildren(currentData);
                      }, 0);

                      return; // 阻止无效的拖拽
                    }

                    // 获取当前的 editData（包含用户的编辑）
                    const currentEditData = HPRef.current?.getEditData?.() || [];

                    // 创建一个 Map 用于快速查找编辑数据（递归构建）
                    const editDataMap = new Map();
                    const buildEditMap = (data: any[]) => {
                      data.forEach((item: any) => {
                        editDataMap.set(String(item.id), item);
                        if (item.children && Array.isArray(item.children)) {
                          buildEditMap(item.children);
                        }
                      });
                    };
                    buildEditMap(currentEditData);

                    // 创建一个 Set 来跟踪已处理的项目ID，防止重复
                    const processedProjectIds = new Set<string>();

                    // 递归合并数据，保留所有用户编辑的字段
                    // 同时清理任何无效的嵌套结构（项目不应该有 children）
                    // 并确保每个项目只出现一次（防止拖拽后重复）
                    const mergeEditData = (records: any[]): any[] => {
                      return records
                        .map((record: any) => {
                          // 跳过 Total 行，稍后单独处理
                          if (record.Project === 'Total' || String(record.id) === 'total') {
                            return record;
                          }

                          const editedRecord = editDataMap.get(String(record.id));

                          // 判断是否为目录：有 children 属性
                          const isCategory = record.children !== undefined;

                          // 如果有编辑记录，合并所有字段（保留用户的所有编辑）
                          const merged = editedRecord
                            ? { ...record, ...editedRecord } // 优先使用编辑数据中的所有字段
                            : { ...record };

                          // 如果这是项目（非目录），确保没有 children
                          if (!isCategory) {
                            // 移除任何 children，因为项目不能有子项
                            if (merged.children) {
                              delete merged.children;
                            }

                            // 检查是否已经处理过这个项目（防止重复）
                            const projectId = String(merged.id);
                            if (processedProjectIds.has(projectId)) {
                              // 这个项目已经在其他地方出现了，跳过这个实例（避免重复）
                              return null;
                            }
                            processedProjectIds.add(projectId);
                          } else {
                            // 对于目录，保留 children 字段（递归处理子项）
                            // 只使用 drag result (record) 的结构，不信任 editData 的结构（可能包含旧位置）
                            if (record.children && Array.isArray(record.children)) {
                              merged.children = mergeEditData(record.children);
                              // 过滤掉 null 值（被标记为重复的项目）
                              merged.children = merged.children.filter((child: any) => child !== null);
                            } else {
                              // 如果 drag result 中没有 children，设置为空数组（不信任 editData 的结构）
                              merged.children = [];
                            }
                          }

                          return merged;
                        })
                        .filter((record: any) => record !== null); // 移除被标记为重复的项目
                    };

                    // 过滤掉 Total 行，处理其余记录
                    const recordsWithoutTotal = records.filter((record: any) =>
                      record.Project !== 'Total' && String(record.id) !== 'total'
                    );
                    const mergedRecords = mergeEditData(recordsWithoutTotal);

                    // 获取 Total 行（如果有）
                    const totalRow = records.find((record: any) =>
                      record.Project === 'Total' || String(record.id) === 'total'
                    );

                    // 清理空目录的 children 数组，确保一致性
                    const cleanupEmptyCategories = (items: any[]): any[] => {
                      return items.map((item: any) => {
                        // 跳过 Total 行
                        if (item.Project === 'Total' || String(item.id) === 'total') {
                          return item;
                        }

                        const isCategory = item.children !== undefined;
                        if (isCategory) {
                          // 确保目录有 children 数组（即使为空）
                          if (!item.children || !Array.isArray(item.children)) {
                            item.children = [];
                          }
                          // 递归清理子目录
                          item.children = cleanupEmptyCategories(item.children);
                        }
                        return item;
                      });
                    };
                    const cleanedRecords = cleanupEmptyCategories(mergedRecords);

                    // 再次验证合并后的数据（不包括 Total 行）
                    const secondValidationResult = validateDragResult(cleanedRecords);
                    if (!secondValidationResult.isValid) {
                      console.warn('Drag validation failed after merge. Reverting drag.', secondValidationResult);

                      let errorMessage = '';
                      switch (secondValidationResult.errorType) {
                        case 'category':
                          errorMessage = 'Invalid drag operation. Categories cannot be nested inside other categories.';
                          break;
                        case 'project':
                          errorMessage = 'Invalid drag operation. Projects cannot be nested inside other projects.';
                          break;
                        default:
                          errorMessage = 'Invalid drag operation. Please try again.';
                      }

                      notification.error({
                        description: errorMessage,
                      });

                      // 使用 setTimeout 确保在拖拽操作完成后才展开（避免与 draggable addon 的 onExpandedKeysChange 冲突）
                      setTimeout(() => {
                        const currentData = headcountData.length > 0 ? headcountData : (HPRef.current?.getTableData?.() || []);
                        console.log('🔄 [DEBUG] Expanding categories after merge validation failure, data length:', currentData.length);
                        expandAllCategoriesWithChildren(currentData);
                      }, 0);

                      return;
                    }

                    // 重新计算一级目录的汇总数据（因为拖拽可能改变了子项目的位置）
                    // recalculateStatusTotals 已经会在第一位返回 Total 行
                    const recalculatedData = recalculateStatusTotals(cleanedRecords);

                    // 确保 Total 行始终在第一位（recalculateStatusTotals 应该已经包含，但为了安全起见再次检查）
                    let finalData = recalculatedData;
                    const totalIndex = recalculatedData.findIndex((item: any) =>
                      item.Project === 'Total' || String(item.id) === 'total'
                    );

                    if (totalIndex > 0) {
                      // 如果 Total 不在第一位，将其移到第一位
                      const total = recalculatedData[totalIndex];
                      finalData = [total, ...recalculatedData.filter((item: any) =>
                        item.Project !== 'Total' && String(item.id) !== 'total'
                      )];
                    } else if (totalIndex === -1) {
                      // 如果 Total 不存在，从原始数据中获取并放在第一位
                      const currentData = headcountData.length > 0 ? headcountData : (HPRef.current?.getTableData?.() || []);
                      const total = currentData.find((item: any) =>
                        item.Project === 'Total' || String(item.id) === 'total'
                      );
                      if (total) {
                        finalData = [total, ...recalculatedData];
                      }
                    }

                    // 更新数据
                    setHeadcountData(finalData);
                    if (HPRef.current) {
                      HPRef.current.setEditData(finalData);
                      HPRef.current.setTableData(finalData);
                    }

                    // 使用setTimeout确保更新完成后再展开所有目录
                    setTimeout(() => {
                      // 展开所有有 children 的目录（操作完成后统一处理）
                      expandAllCategoriesWithChildren(finalData);
                    }, 100);
                  } catch (error) {
                    // 如果发生任何错误，也要确保展开所有目录
                    console.error('Error in onDragEnd:', error);
                    setTimeout(() => {
                      expandAllCategoriesWithChildren(originalData);
                    }, 100);
                  }
                },
              }),
            ]}
            shouldRecordDraggable={(record: any) => {
              // "Total" 不能被拖拽
              return record.Project !== 'Total' && String(record.id) !== 'total';
            }}
            onSave={handleSave}
            onCancelRefetch={fetchProjectHeadcountData}
            originalData={originalHeadcountData}
            emptyContent={<div style={{ minHeight: '200px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <NoData text={formatMessage({ id: 'dbs_empty_text' })} />
            </div>}
          ></TableContent>
        </CardCommon>
      </div>
      <PlainText
        url="/api/v1/mgmt_pc/note/get"
        editUrl="/api/v1/mgmt_pc/note/update"
        params={{
          source: "headcount_appendix",
          logical_flag: "has_date_latest"
        }}
        selectedMonth={selectedMonthMomentAppendix}
        maxDataTime={maxDataTime}
        showTimeInTitle={true}
        editable={true}
        userPermissions={{ canEdit: buttonPermission.Appendix, noAccessHide: getAppendixNotesPermission('publishing_headcount_Appendix&Notes') }}
        tipString={'Data is updated by the Business Operations Management team.'}
        onEditingChange={handleAppendixNotesEditingChange}
        onEditClick={(type) => {
          // Track buried point for Appendix Notes Edit button or Add Content link click
          const extInfo = type === 'Add Content Link'
            ? 'Appendix Notes Edit - Add Content Link'
            : 'Appendix Notes Edit - Edit Button';
          const btnPoint = createBtnPoint(
            PublishingHeadcountTab,
            'appendixNotes',
            extInfo
          );
          if (btnPoint) {
            SGBuriedBtnPoint(btnPoint);
          }
        }}
        // Auto-save feature
        autoSavePageKey={EDIT_PAGE_KEY_MAP.publishing_headcount_appendix_notes}
      />
      {/* Auto-save Modal for Headcount Project Table */}
      <AutoSaveModal
        visible={autoSaveVisible}
        onConfirm={handleAutoSaveConfirm}
        onClose={handleAutoSaveCancel}
      />

      {/* Route Prompt for unsaved changes */}
      <RoutePrompt
        when={hasHeadcountUnsavedChanges}
        onOk={async () => {
          await HPRef.current?.handleSave();
        }}
      />
    </div>
  );
};